import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import Grid from "@mui/material/Grid2";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Tabs from "@common/components/Tabs";
import Tab from "@common/components/Tab";
import Typography from "@common/components/Typography";
import FavoritesTab from "./FavoritesTab";
import DirectoryTab from "./DirectoryTab";
import RecentTab from "./RecentTab";
import OtherTab from "./OtherTab";
import { TextField, Switch } from "@mui/material";
import { getRoomsByDisplayName, getRoomsByLocation } from "@services/meetings";
import { Room } from "@services/rooms/types";

type RoomSelectionProps = {};

const RoomSelection = (props: RoomSelectionProps) => {
  const {} = props;
  const context = React.useContext(RoomsRealtimeContext);
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [tab, setTab] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [excludeExternal, setExcludeExternal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [cachedLocations, setCachedLocations] = useState<{
    [locationId: string]: Room[];
  }>({});

  const fetchRooms = useCallback(
    async (locationId: string) => {
      if (cachedLocations[locationId] === undefined) {
        try {
          const rooms = (
            await getRoomsByLocation(
              context.detail?.meeting?.account?.id,
              locationId
            )
          ).filter((room) => true);
          setCachedLocations({ ...cachedLocations, [locationId]: rooms });
        } catch (e) {
          dispatchError(e);
        }
      }
    },
    [cachedLocations]
  );

  useEffect(() => {
    if (search !== "") {
      setLoading(true);
      const getData = setTimeout(async () => {
        try {
          const rooms = await getRoomsByDisplayName(
            context.detail?.meeting?.account?.id,
            search
          );
          rooms.forEach((room) => {
            if (room?.location?.id) fetchRooms(room.location.id);
          });
          setLoading(false);
        } catch (e) {
          dispatchError(e);
        }
      }, 1000);
      return () => clearTimeout(getData);
    }
    setLoading(false);
    return;
  }, [search]);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      sx={{ width: '100%', gap: "1rem",
        height: "100%", }}
    >
      <Grid>
        <Grid container direction="row" sx={{ width: '100%' }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            {["Directory", "Favorites", "Recent", "Other"].map((value) => (
              <Tab key={value} label={t(value)} />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      {tab !== 3 && (
        <>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t("Search")}
            sx={{ width: "100%" }}
          />
          <Grid
            container
            direction="row"
            sx={{ width: '100%', justifyContent: "end",
              gap: "0.5rem", }}
          >
            <Switch
              value={excludeExternal}
              onChange={(_, value) => setExcludeExternal(value)}
            />
            <Typography size="medium">
              {t(`Exclude unmanaged rooms`)}
            </Typography>
          </Grid>
        </>
      )}
      <Grid
        sx={{
          height: "100%",
          overflow: "overlay",
        }}>
        {
          {
            0: (
              <DirectoryTab
                search={search}
                excludeExternal={excludeExternal}
                cachedLocations={cachedLocations}
                fetchRooms={fetchRooms}
                loading={loading}
              />
            ),
            1: (
              <FavoritesTab search={search} excludeExternal={excludeExternal} />
            ),
            2: <RecentTab search={search} excludeExternal={excludeExternal} />,
            3: <OtherTab />,
          }[tab]
        }
      </Grid>
    </Grid>
  );
};

export default RoomSelection;
